/**
 * `<SurfaceLayout>` / `<Panel>` — the shared surface + panel chrome (frontend.md §2.1).
 *
 * Every surface root (`*Surface`) wraps its content in `<SurfaceLayout>` for a consistent
 * header (title + an optional action slot) and body region. Panels within a surface use
 * `<Panel>` for a consistent titled section. This keeps the surface files focused on their
 * data + interaction, not on chrome boilerplate.
 */

import type { ReactNode } from 'react';

interface SurfaceLayoutProps {
  title: string;
  /** Right-aligned header slot — surface-level controls (mode toggle, scope controls). */
  headerActions?: ReactNode;
  children: ReactNode;
}

export function SurfaceLayout({
  title,
  headerActions,
  children,
}: SurfaceLayoutProps): JSX.Element {
  return (
    <section className="surface">
      <header className="surface__header">
        <h1 className="surface__title">{title}</h1>
        {headerActions !== undefined && (
          <div className="surface__header-actions">{headerActions}</div>
        )}
      </header>
      <div className="surface__body">{children}</div>
    </section>
  );
}

interface PanelProps {
  title: string;
  /** Right-aligned panel header slot — panel-level controls. */
  actions?: ReactNode;
  /** A deep-link anchor id — `<CorpusHealthSurface>` uses `?panel=` to scroll to one. */
  anchorId?: string;
  children: ReactNode;
}

export function Panel({ title, actions, anchorId, children }: PanelProps): JSX.Element {
  return (
    <div className="panel" id={anchorId}>
      <header className="panel__header">
        <h2 className="panel__title">{title}</h2>
        {actions !== undefined && <div className="panel__actions">{actions}</div>}
      </header>
      <div className="panel__body">{children}</div>
    </div>
  );
}
